import { User } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import {
  type AdminHouseholdDetail,
  type AdminHouseholdUpdate,
  useUpdateHousehold,
} from "@/lib/api";

import { InlineEdit } from "./inline-edit";
import { MissingChip } from "./missing-chip";

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="border-border-subtle flex items-start justify-between gap-3.5 border-b py-2.5 text-[13.5px] last:border-b-0">
      <span className="text-muted whitespace-nowrap">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

function formatSubmittedAt(submittedAt: string | null): string {
  if (!submittedAt) {
    return "—";
  }
  return new Date(submittedAt).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

interface ContactCardProps {
  household: AdminHouseholdDetail;
}

/** Rail card with the household's resident, address, and submission details — click any value to edit. */
export function ContactCard({ household }: ContactCardProps) {
  const updateMutation = useUpdateHousehold();

  const savePatch = (patch: AdminHouseholdUpdate) =>
    updateMutation.mutateAsync({ id: household.id, body: patch }).then(() => undefined);

  return (
    <div className="bg-card shadow-elevation rounded-md">
      <div className="border-border-subtle flex items-center gap-2.5 border-b px-5 py-[18px]">
        <User className="text-muted size-[17px]" weight="light" />
        <h2 className="text-primary text-[17px] font-bold">Contact &amp; location</h2>
      </div>
      <div className="px-5 pt-3.5 pb-4.5">
        <Row
          label="Resident"
          value={
            <InlineEdit
              value={household.contact_name ?? ""}
              onSave={(v) => savePatch({ contact_name: v || null })}
              ariaLabel="Resident name"
              placeholder="Add name"
            >
              {household.contact_name ?? <span className="text-faint">—</span>}
            </InlineEdit>
          }
        />
        <Row
          label="Email"
          value={
            <span className="inline-flex items-center gap-2">
              {(!household.contact_email || !household.contact_email.trim()) && (
                <MissingChip variant="handoff" />
              )}
              <InlineEdit
                value={household.contact_email}
                onSave={(v) => (v.trim() ? savePatch({ contact_email: v }) : Promise.resolve())}
                ariaLabel="Contact email"
                className="font-mono text-[12.5px]"
              >
                <span className="font-mono text-[12.5px] break-all">{household.contact_email}</span>
              </InlineEdit>
            </span>
          }
        />
        <Row
          label="Address"
          value={
            <InlineEdit
              value={household.address ?? ""}
              onSave={(v) => savePatch({ address: v || null })}
              ariaLabel="Street address"
              placeholder="Add address"
            >
              {household.address ?? <span className="text-faint">—</span>}
            </InlineEdit>
          }
        />
        <Row
          label="City"
          value={
            <InlineEdit
              value={household.city ?? ""}
              onSave={(v) => savePatch({ city: v || null })}
              ariaLabel="City"
              placeholder="Add city"
            >
              {household.city ?? <span className="text-faint">—</span>}
            </InlineEdit>
          }
        />
        <Row
          label="Postcode"
          value={
            <InlineEdit
              value={household.postcode ?? ""}
              onSave={(v) => savePatch({ postcode: v || null })}
              ariaLabel="Postcode"
              placeholder="Add postcode"
            >
              {household.postcode ?? <span className="text-faint">—</span>}
            </InlineEdit>
          }
        />
        <Row
          label="Region"
          value={
            <InlineEdit
              value={household.region ?? ""}
              onSave={(v) => savePatch({ region: v || null })}
              ariaLabel="Region"
              placeholder="Add region"
            >
              {household.region ?? <span className="text-faint">—</span>}
            </InlineEdit>
          }
        />
        <Row
          label="Country"
          value={
            <InlineEdit
              value={household.country_code ?? ""}
              onSave={(v) => savePatch({ country_code: v || null })}
              ariaLabel="Country code"
              placeholder="Add country code"
              className="font-mono text-[12.5px]"
            >
              {household.country_code ?? <span className="text-faint">—</span>}
            </InlineEdit>
          }
        />
        <Row
          label="Coordinates"
          value={
            <span className="inline-flex items-center gap-1.5">
              <span className="text-muted type-label">Lat</span>
              <InlineEdit
                value={household.latitude != null ? String(household.latitude) : ""}
                type="number"
                onSave={(v) => savePatch({ latitude: v === "" ? null : Number(v) })}
                ariaLabel="Latitude"
                placeholder="—"
                className="font-mono text-[12.5px]"
              >
                <span className="font-mono text-[12.5px]">
                  {household.latitude != null ? household.latitude.toFixed(3) : "—"}
                </span>
              </InlineEdit>
              <span className="text-faint">·</span>
              <span className="text-muted type-label">Lng</span>
              <InlineEdit
                value={household.longitude != null ? String(household.longitude) : ""}
                type="number"
                onSave={(v) => savePatch({ longitude: v === "" ? null : Number(v) })}
                ariaLabel="Longitude"
                placeholder="—"
                className="font-mono text-[12.5px]"
              >
                <span className="font-mono text-[12.5px]">
                  {household.longitude != null ? household.longitude.toFixed(3) : "—"}
                </span>
              </InlineEdit>
            </span>
          }
        />
        <Row label="Submitted" value={formatSubmittedAt(household.submitted_at)} />
      </div>
    </div>
  );
}
